/**
 * useFollowers Hook (Shared)
 * Get followers for a user (by displayName)
 * Platform-agnostic — uses HttpClient interface
 */

import { useCallback, useEffect, useState } from 'react';
import type { HttpClient } from '../api/httpClient';
import { ApiError } from '../api/httpClient';
import type { PaginatedUserList, SearchResult } from '../socialTypes';

// =============================================================================
// Types
// =============================================================================

export interface UseFollowersOptions {
  httpClient: HttpClient;
  displayName: string | undefined;
}

export interface UseFollowersResult {
  followers: SearchResult[];
  loading: boolean;
  error: string | null;
  total: number;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  reload: () => void;
}

const PAGE_SIZE = 20;

// =============================================================================
// Hook
// =============================================================================

export function useFollowers({ httpClient, displayName }: UseFollowersOptions): UseFollowersResult {
  const [followers, setFollowers] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);

  const loadFollowers = useCallback(
    async (currentOffset: number, append: boolean = false) => {
      if (!displayName) return;

      setLoading(true);
      setError(null);

      try {
        const data = await httpClient.get<PaginatedUserList>(
          `/api/users/${encodeURIComponent(displayName)}/followers`,
          { limit: PAGE_SIZE, offset: currentOffset },
        );

        setTotal(data.total);
        setFollowers((prev) => (append ? [...prev, ...data.users] : data.users));
        setOffset(currentOffset);
      } catch (err) {
        const errorMsg = ApiError.getUserMessage(err, 'Failed to load followers');
        setError(errorMsg);
        console.error('Failed to load followers:', err);
      } finally {
        setLoading(false);
      }
    },
    [displayName, httpClient],
  );

  // Load initial followers when displayName changes
  useEffect(() => {
    if (displayName) {
      setFollowers([]);
      setOffset(0);
      loadFollowers(0, false);
    }
  }, [displayName, loadFollowers]);

  const loadMore = useCallback(async () => {
    const nextOffset = offset + PAGE_SIZE;
    await loadFollowers(nextOffset, true);
  }, [offset, loadFollowers]);

  const reload = useCallback(() => {
    setFollowers([]);
    setOffset(0);
    loadFollowers(0, false);
  }, [loadFollowers]);

  const hasMore = followers.length < total;

  return {
    followers,
    loading,
    error,
    total,
    hasMore,
    loadMore,
    reload,
  };
}
